// src/pages/Notifications.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import { notificationService } from '../services/notificationService';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useNavigate } from 'react-router-dom';

const NotificationsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(notificationService.getNotifications());

  useEffect(() => {
    const unsubscribe = notificationService.subscribe((newNotifications) => {
      setNotifications(newNotifications);
    });
    return () => unsubscribe();
  }, []);

  const getIcon = (type: string) => {
    switch (type) {
      case 'warning':
        return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
      case 'error':
        return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
      case 'success':
        return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
      default:
        return <InfoIcon sx={{ color: theme.palette.info.main }} />;
    }
  };

  const handleNotificationClick = (notification: any) => {
    notificationService.markAsRead(notification.id);
    if (notification.action?.link) {
      navigate(notification.action.link);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight={800}>
          <span style={{ color: '#214F80' }}>Notifications</span>
        </Typography>
        <Box>
          {notifications.filter(n => !n.read).length > 0 && (
            <Button onClick={() => notificationService.markAllAsRead()} sx={{ mr: 1 }}>
              <DoneAllIcon sx={{ mr: 0.5 }} /> Mark all read
            </Button>
          )}
          {notifications.length > 0 && (
            <Button onClick={() => notificationService.clearAll()} color="error">
              <DeleteSweepIcon sx={{ mr: 0.5 }} /> Clear all
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {notifications.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          notifications.map((notification, index) => (
            <Box key={notification.id}>
              <Box
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  cursor: 'pointer',
                  backgroundColor: notification.read ? 'transparent' : alpha(theme.palette.primary.main, 0.04),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <Box sx={{ mt: 0.5 }}>{getIcon(notification.type)}</Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={notification.read ? 400 : 600}>
                    {notification.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {notification.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {notification.createdAt.toLocaleDateString()} {notification.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>
              {index < notifications.length - 1 && <Divider />}
            </Box>
          ))
        )}
      </Paper>
    </Container>
  );
};

export default NotificationsPage;